/** Locale resolution: the landing's own path wins, then cookie, then Accept-Language, then English. */
export const LOCALES = ["en", "ru"] as const;

export type Locale = (typeof LOCALES)[number];

export const LOCALE_COOKIE = "sm-lang";

/**
 * The public marketing URLs — one per language, which is the whole point.
 *
 * These paths are also the two hreflang alternates and the two sitemap
 * entries; keeping the map here means the routes, the head and the sitemap
 * cannot disagree about where the Russian page lives.
 */
export const LANDING_PATHS: Record<Locale, string> = { en: "/", ru: "/ru" };

/**
 * The three published legal documents, page then locale.
 *
 * Mirrors `LandingHelper::LEGAL_PATHS` on the Rails side, which is what
 * actually serves them, and the iOS app's `LegalPage` — three copies of one
 * fact, because three clients each need it and none of them can read the
 * others. All three must agree that the Russian privacy policy is at
 * `/ru/privacy`; `legalUrl` is the only thing in this app that should build
 * one of these.
 */
export const LEGAL_PATHS = {
  privacy: { en: "/privacy", ru: "/ru/privacy" },
  terms: { en: "/terms", ru: "/ru/terms" },
  support: { en: "/support", ru: "/ru/support" },
} as const satisfies Record<string, Record<Locale, string>>;

export type LegalPage = keyof typeof LEGAL_PATHS;

/**
 * Where a legal document lives, absolute.
 *
 * On the MARKETING origin, never this app's: slowmad.club serves these and
 * app.slowmad.club does not, so a relative href here would 404 in
 * production. `siteBaseUrl` is already the name for that distinction — see
 * `src/lib/env.ts`.
 */
export function legalUrl(page: LegalPage, locale: Locale, siteBaseUrl: string): string {
  return `${siteBaseUrl}${LEGAL_PATHS[page][locale]}`;
}

/** True for the two landing URLs, which force Ticket and take their locale from the path. */
export function isLandingPath(pathname: string): boolean {
  return pathname === LANDING_PATHS.en || pathname === LANDING_PATHS.ru;
}

/**
 * The language a landing URL serves, or null for every other route.
 *
 * Each landing URL is one language, for everyone — `/` stays English even
 * for a browser asking for Russian. A URL that serves different text to
 * different visitors is a URL a crawler indexes in one language while users
 * read another, which defeats having two addresses at all. Off the landing
 * there is no such constraint: those routes are noindex.
 */
function localeFromPath(pathname: string): Locale | null {
  if (pathname === LANDING_PATHS.ru) return "ru";
  if (pathname === LANDING_PATHS.en) return "en";
  return null;
}

function isLocale(value: string): value is Locale {
  return (LOCALES as readonly string[]).includes(value);
}

export function pickLocale(
  cookieHeader: string | null,
  acceptLanguage: string | null,
  pathname?: string,
): Locale {
  // Before the cookie, not after it: on the landing the URL is the answer,
  // and a stale cookie must not make /ru render English.
  const fromPath = pathname === undefined ? null : localeFromPath(pathname);
  if (fromPath) return fromPath;

  const cookie = cookieHeader?.match(/(?:^|;\s*)sm-lang=([a-zA-Z-]+)(?:;|$)/)?.[1]?.toLowerCase();
  if (cookie && isLocale(cookie)) return cookie;

  // "ru-RU,ru;q=0.9,en;q=0.8" → primary subtags in descending q-order.
  const candidates = (acceptLanguage ?? "")
    .split(",")
    .map((part) => {
      const [tag = "", ...params] = part.trim().split(";");
      const q = Number(params.find((p) => p.trim().startsWith("q="))?.split("=")[1] ?? "1");
      return { primary: tag.trim().toLowerCase().split("-")[0] ?? "", q: Number.isNaN(q) ? 0 : q };
    })
    .filter((c) => c.primary.length > 0)
    .sort((a, b) => b.q - a.q);

  for (const candidate of candidates) {
    if (isLocale(candidate.primary)) return candidate.primary;
  }
  return "en";
}

/**
 * The locale a route's `meta()` should use, read off the ROOT match.
 *
 * Every route's `meta()` runs before any provider is in scope, so it needs
 * its own resolution — but `app/root.tsx`'s loader has already done exactly
 * that for this request, and root is the one match that is always present.
 * A per-route loader is NOT equivalent: React Router calls `meta()` with no
 * `loaderData` for a route rendering a `HydrateFallback` during SSR, which
 * was `/auth/google/callback` — a route since deleted with the PKCE
 * redirect flow, though the finding is about React Router and outlives it.
 * That route shipped an English title to Russian visitors for exactly this
 * reason, beside a correct `<html lang="ru">` produced from this same
 * cookie, and the next HydrateFallback route would do the same.
 *
 * Typegen types root's `loaderData` as always present, and in every state
 * React Router actually produces it is. The optional chaining is for the
 * one that would be expensive rather than impossible: if root's loader ever
 * throws, `meta()` is still called while the error boundary renders, and a
 * `TypeError` raised from inside `meta()` turns a recoverable error page
 * into a failed render. English is the right answer there; a crash is not.
 */
export function localeFromRootMatch(
  rootMatch: { loaderData?: { locale?: unknown } | undefined } | undefined,
): Locale {
  const locale = rootMatch?.loaderData?.locale;
  return typeof locale === "string" && isLocale(locale) ? locale : "en";
}
